import { abortError, isAbortError, maybeYield } from '../utils';
import {
    EvaluationJob,
    EvaluationJobResult,
    VolumeEvaluationRequest,
    coarseDimensions,
    coarsePassApplies,
    evaluateJob,
} from './crossSectionFieldSampling';

const MAX_WORKERS = 8;
const JOBS_PER_WORKER = 3;

function concat(parts: readonly Float32Array[], length: number): Float32Array {
    const out = new Float32Array(length);
    let offset = 0;
    for (const part of parts) {
        out.set(part, offset);
        offset += part.length;
    }
    return out;
}

/** The coarse pass, when it applies, then the fine pass, each split into at most `parts` slabs run in order. */
async function evaluateBySlabs(
    request: VolumeEvaluationRequest,
    parts: number,
    run: (jobs: EvaluationJob[]) => Promise<Float32Array[]>
): Promise<Float32Array> {
    const pass = (
        kind: EvaluationJob['kind'],
        count: number,
        coarse: Float32Array | null
    ): Promise<Float32Array[]> => {
        const size = Math.max(1, Math.ceil(count / parts));
        const jobs: EvaluationJob[] = [];
        for (let from = 0; from < count; from += size) {
            const to = Math.min(count, from + size);
            jobs.push({ id: jobs.length, kind, request, coarse, from, to });
        }
        return run(jobs);
    };
    const [nx, ny, nz] = request.sizePx;
    let coarse: Float32Array | null = null;
    if (coarsePassApplies(request)) {
        const [cx, cy, cz] = coarseDimensions(request.sizePx, request.stride);
        coarse = concat(await pass('coarse', cz, null), cx * cy * cz);
    }
    return concat(await pass('fine', nz, coarse), nx * ny * nz);
}

function evaluateInThread(
    request: VolumeEvaluationRequest,
    signal?: AbortSignal
): Promise<Float32Array> {
    let tick = performance.now();
    return evaluateBySlabs(request, Infinity, async (jobs) => {
        const slabs: Float32Array[] = [];
        for (const job of jobs) {
            slabs.push(evaluateJob(job));
            tick = await maybeYield(tick, signal);
        }
        return slabs;
    });
}

let pool: Worker[] | null = null;
let busy = false;
let releasePending = false;

/** A constructor that throws terminates the workers already built and rethrows, leaving no pool. */
function acquirePool(): Worker[] {
    if (pool) {
        return pool;
    }
    const built: Worker[] = [];
    try {
        const cores =
            typeof navigator !== 'undefined' && navigator.hardwareConcurrency
                ? navigator.hardwareConcurrency
                : 2;
        const size = Math.max(1, Math.min(MAX_WORKERS, cores - 1));
        for (let i = 0; i < size; i++) {
            built.push(
                new Worker(
                    new URL(
                        './crossSectionFieldSampling.worker.ts',
                        import.meta.url
                    ),
                    { type: 'module' }
                )
            );
        }
    } catch (error) {
        built.forEach((worker) => worker.terminate());
        throw error;
    }
    pool = built;
    return built;
}

function destroyPool(): void {
    pool?.forEach((worker) => worker.terminate());
    pool = null;
}

/**
 * Run jobs across the pool, each worker taking the next as it frees up. Any
 * worker error, undeserialisable reply, reply to another job, failed post or
 * abort rejects the run and terminates the pool.
 */
function runJobs(
    jobs: readonly EvaluationJob[],
    signal?: AbortSignal
): Promise<Float32Array[]> {
    return new Promise((resolve, reject) => {
        const results: Float32Array[] = new Array(jobs.length);
        let next = 0;
        let remaining = jobs.length;
        let settled = false;
        const onAbort = (): void => fail(abortError());
        const fail = (error: unknown): void => {
            if (settled) {
                return;
            }
            settled = true;
            signal?.removeEventListener('abort', onAbort);
            destroyPool();
            reject(error);
        };
        const finish = (): void => {
            settled = true;
            signal?.removeEventListener('abort', onAbort);
            resolve(results);
        };
        if (signal?.aborted) {
            fail(abortError());
            return;
        }
        if (jobs.length === 0) {
            finish();
            return;
        }
        signal?.addEventListener('abort', onAbort, { once: true });
        // Every worker gets its handlers before any job is posted, so an idle worker's error also fails the run.
        const feeders = acquirePool().map((worker) => {
            let current = -1;
            const feed = (): void => {
                if (settled || next >= jobs.length) {
                    current = -1;
                    return;
                }
                current = next++;
                try {
                    worker.postMessage(jobs[current]);
                } catch (error) {
                    fail(error);
                }
            };
            worker.onmessage = (event: MessageEvent<EvaluationJobResult>) => {
                if (settled) {
                    return;
                }
                const { id, values } = event.data;
                if (id !== current) {
                    fail(
                        new Error(
                            `Field evaluation worker answered job ${id}, ` +
                                (current < 0
                                    ? 'while idle'
                                    : `expected job ${current}`)
                        )
                    );
                    return;
                }
                results[current] = values;
                remaining--;
                if (remaining === 0) {
                    finish();
                } else {
                    feed();
                }
            };
            worker.onmessageerror = () =>
                fail(
                    new Error(
                        current < 0
                            ? 'An idle field evaluation worker sent a reply that could not be deserialised'
                            : `Field evaluation worker reply to job ${current} could not be deserialised`
                    )
                );
            worker.onerror = (event: ErrorEvent) => {
                event.preventDefault();
                fail(event.error ?? new Error(event.message));
            };
            return feed;
        });
        feeders.forEach((feed) => feed());
    });
}

/** Terminate the workers now, or when the running evaluation ends; the next evaluation builds a new pool. */
export function releaseFieldSamplingWorkers(): void {
    if (busy) {
        releasePending = true;
    } else {
        destroyPool();
    }
}

/**
 * Evaluate a whole volume of clamped signed distances in mm, x fastest, in
 * Web Workers when available. A worker failure or a pool busy with another
 * volume falls back to the calling thread; an abort rejects with an
 * `AbortError`.
 */
export async function evaluateVolume(
    request: VolumeEvaluationRequest,
    signal?: AbortSignal
): Promise<Float32Array> {
    if (typeof Worker === 'undefined') {
        return evaluateInThread(request, signal);
    }
    try {
        // The pool never runs two volumes at once.
        if (busy) {
            throw new Error(
                'Field evaluation workers are busy with another volume'
            );
        }
        busy = true;
        try {
            const parts = acquirePool().length * JOBS_PER_WORKER;
            return await evaluateBySlabs(request, parts, (jobs) =>
                runJobs(jobs, signal)
            );
        } finally {
            busy = false;
            if (releasePending) {
                releasePending = false;
                destroyPool();
            }
        }
    } catch (error) {
        if (isAbortError(error)) {
            throw error;
        }
        console.warn(
            'Field evaluation workers failed; evaluating on the calling thread',
            error
        );
        return evaluateInThread(request, signal);
    }
}
